import {
  createUserSchema,
  roles,
  updateUserSchema,
  type Me,
  type PasswordLink,
  type RegionSummary,
  type Role,
  type UserSummary,
} from '@smarton-pbx/shared';
import { awayAt } from '@smarton-pbx/shared';
import { KeyRound, Link2, MoreHorizontal, Pencil, PhoneForwarded, Plus, Search, ShieldOff, Users } from 'lucide-react';
import { type FormEvent, useMemo, useState } from 'react';
import { api, photoUrl } from '../api';
import { Avatar, CopyLink, Dialog, EmptyState, Field, Loading, Menu, Notice, opensOnClick, PageHeader, Pill } from '../components/ui';
import { t, tc } from '../i18n';
import { useLoad, useSubmit } from '../lib/hooks';
import { type Column, Cells, mayDownload, TableHead, useTable } from '../components/table';
import { formatNumber } from '../lib/calls';
import { CallRulesCard } from './CallRules';
import { when } from './Calls';
import { PhotoControls } from './PhotoControls';

export const roleNames: Record<Role, string> = {
  TENANT_ADMIN: t('Admin'),
  REGION_MANAGER: t('Region manager'),
  AGENT: t('Agent'),
};

type Editing =
  | { kind: 'new' }
  | { kind: 'edit'; user: UserSummary }
  | { kind: 'link'; user: UserSummary; link: PasswordLink }
  | { kind: 'rules'; user: UserSummary };

/** A person's status: its words, and its pill's colour. */
function status(user: UserSummary): { label: string; kind?: 'warning' | 'success' } {
  if (user.disabled) return { label: t('Disabled') };
  if (!user.hasPassword) return { label: t('Invited'), kind: 'warning' };
  const away = { awayFrom: user.awayFrom ? new Date(user.awayFrom) : null, awayUntil: user.awayUntil ? new Date(user.awayUntil) : null };
  if (awayAt(away, new Date())) return { label: t('Away'), kind: 'warning' };
  return { label: tc('person', 'Active'), kind: 'success' };
}

function StatusPill({ user }: { user: UserSummary }) {
  const { label, kind } = status(user);
  return (
    <Pill {...(kind && { kind })} dot>
      {label}
    </Pill>
  );
}

function peopleColumns(): Column<UserSummary>[] {
  return [
    {
      key: 'name',
      label: t('Name'),
      fixed: true,
      cell: (user) => (
        <div className="person">
          <Avatar name={user.name} photo={photoUrl(user.id, user.photoVersion)} />
          <div>
            <div className="name">{user.name}</div>
            <div className="detail">{user.email}</div>
          </div>
        </div>
      ),
      excel: [
        { label: t('Name'), value: (user) => user.name },
        { label: t('Email'), value: (user) => user.email },
      ],
    },
    { key: 'extension', label: t('Extension'), className: 'tabular', cell: (user) => user.extension, value: (user) => user.extension },
    {
      key: 'role',
      label: t('Role'),
      cell: (user) => (user.role === 'TENANT_ADMIN' ? <Pill kind="brand">{roleNames[user.role]}</Pill> : roleNames[user.role]),
      value: (user) => roleNames[user.role],
    },
    {
      key: 'region',
      label: t('Region'),
      className: 'hide-mobile',
      cell: (user) => user.regionName ?? <span className="subtle">{t('All regions')}</span>,
      value: (user) => user.regionName ?? t('All regions'),
    },
    { key: 'status', label: t('Status'), className: 'hide-mobile', cell: (user) => <StatusPill user={user} />, value: (user) => status(user).label },
    {
      key: 'mobile',
      label: t('Mobile number'),
      hidden: true,
      className: 'hide-mobile tabular',
      cell: (user) => (user.mobileNumber ? formatNumber(user.mobileNumber) : '—'),
      value: (user) => user.mobileNumber,
    },
    {
      key: 'lastSignIn',
      label: t('Last sign-in'),
      hidden: true,
      className: 'hide-mobile',
      cell: (user) => (user.lastSignInAt ? when(user.lastSignInAt) : '—'),
      value: (user) => (user.lastSignInAt ? new Date(user.lastSignInAt) : null),
    },
    {
      key: 'mfa',
      label: t('2-step sign-in'),
      hidden: true,
      className: 'hide-mobile',
      cell: (user) => (user.mfaEnabled ? t('On') : t('Off')),
      value: (user) => (user.mfaEnabled ? t('On') : t('Off')),
    },
  ];
}

export function People({ me }: { me: Me }) {
  const users = useLoad(api.listUsers);
  const regions = useLoad(api.listRegions);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [message, setMessage] = useState<string>();
  const [query, setQuery] = useState('');
  const isAdmin = me.role === 'TENANT_ADMIN';

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return users.data ?? [];
    return (users.data ?? []).filter((user) =>
      [user.name, user.email, user.extension, user.regionName ?? ''].some((value) => value.toLowerCase().includes(q)),
    );
  }, [users.data, query]);
  const table = useTable('people', peopleColumns(), mayDownload(me.role) ? { name: t('People'), rows: () => shown } : null);

  async function passwordLink(user: UserSummary) {
    setEditing({ kind: 'link', user, link: await api.passwordLink(user.id) });
  }

  async function resetMfa(user: UserSummary) {
    if (!confirm(t('Reset the 2-step sign-in for {name}? They set it up again at their next sign-in.', { name: user.name }))) return;
    await api.resetMfa(user.id);
    setMessage(t('{name} will set up their authenticator app again at the next sign-in.', { name: user.name }));
    users.reload();
  }

  return (
    <div className="stack">
      <PageHeader title={t('People')} description={isAdmin ? t('Everyone who can sign in, and their extensions.') : t('The people in your region.')}>
        {isAdmin && (
          <button type="button" onClick={() => setEditing({ kind: 'new' })} data-testid="add-person">
            <Plus size={16} aria-hidden /> {t('Add person')}
          </button>
        )}
      </PageHeader>
      {message && <Notice kind="success">{message}</Notice>}
      {users.error && <Notice kind="error">{users.error}</Notice>}
      {!users.data ? (
        <Loading />
      ) : (
        <div className="card">
          <div className="table-toolbar">
            <div className="input-icon">
              <Search size={16} aria-hidden />
              <input type="search" placeholder={t('Search people')} value={query} onChange={(e) => setQuery(e.target.value)} aria-label={t('Search people')} />
            </div>
            <span className="subtle">{t('{n} of {total}', { n: shown.length, total: users.data.length })}</span>
          </div>
          {shown.length === 0 ? (
            <EmptyState icon={Users} title={query ? t('No one matches') : t('No people yet')}>
              {!query && <p>{t('Add the people who make and take calls. Each gets an invite link.')}</p>}
            </EmptyState>
          ) : (
            <div className="table-wrap">
              <table>
                <TableHead table={table} />
                <tbody>
                  {shown.map((user) => (
                    <tr key={user.id} data-testid="person-row" {...(isAdmin && opensOnClick(() => setEditing({ kind: 'edit', user })))}>
                      <Cells table={table} row={user} />
                      <td className="actions">
                        {isAdmin && (
                          <Menu
                            label={t('Actions for {name}', { name: user.name })}
                            trigger={(props) => (
                              <button type="button" className="ghost icon" aria-label={t('Actions for {name}', { name: user.name })} {...props}>
                                <MoreHorizontal size={18} aria-hidden />
                              </button>
                            )}
                          >
                            {(close) => (
                              <>
                                <button type="button" role="menuitem" onClick={() => (close(), setEditing({ kind: 'edit', user }))}>
                                  <Pencil size={16} aria-hidden /> {t('Edit')}
                                </button>
                                <button type="button" role="menuitem" onClick={() => (close(), setEditing({ kind: 'rules', user }))}>
                                  <PhoneForwarded size={16} aria-hidden /> {t('When they can’t answer')}
                                </button>
                                <button type="button" role="menuitem" onClick={() => (close(), void passwordLink(user))}>
                                  {user.hasPassword ? <KeyRound size={16} aria-hidden /> : <Link2 size={16} aria-hidden />}
                                  {user.hasPassword ? t('Password reset link') : t('Invite link')}
                                </button>
                                {user.mfaEnabled && user.id !== me.id && (
                                  <button type="button" role="menuitem" onClick={() => (close(), void resetMfa(user))}>
                                    <ShieldOff size={16} aria-hidden /> {t('Reset 2-step sign-in')}
                                  </button>
                                )}
                              </>
                            )}
                          </Menu>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <Dialog
        open={editing !== null}
        onClose={() => setEditing(null)}
        title={
          editing?.kind === 'link'
            ? t('Link for {name}', { name: editing.user.name })
            : editing?.kind === 'edit'
              ? t('Edit {name}', { name: editing.user.name })
              : editing?.kind === 'rules'
                ? editing.user.name
                : t('Add person')
        }
        {...(editing?.kind === 'new' && { description: t('They get a one-time link to set their password.') })}
        wide={editing?.kind === 'rules'}
      >
        {editing?.kind === 'rules' ? (
          <div className="dialog-body stack">
            <CallRulesCard userId={editing.user.id} name={editing.user.name} />
          </div>
        ) : editing?.kind === 'link' ? (
          <>
            <div className="dialog-body">
              <CopyLink {...editing.link} to={editing.user.email} />
            </div>
            <div className="dialog-footer">
              <button type="button" onClick={() => setEditing(null)}>
                {t('Done')}
              </button>
            </div>
          </>
        ) : editing ? (
          <PersonForm
            user={editing.kind === 'edit' ? editing.user : null}
            regions={regions.data ?? []}
            onCancel={() => setEditing(null)}
            onPhotoChanged={users.reload}
            onSaved={(user, link) => {
              users.reload();
              setEditing(link ? { kind: 'link', user, link } : null);
            }}
          />
        ) : null}
      </Dialog>
    </div>
  );
}

function PersonForm({
  user,
  regions,
  onCancel,
  onPhotoChanged,
  onSaved,
}: {
  user: UserSummary | null;
  regions: RegionSummary[];
  onCancel: () => void;
  /** The photo saves at once, apart from the form. */
  onPhotoChanged: () => void;
  onSaved: (user: UserSummary, link?: PasswordLink) => void;
}) {
  const [name, setName] = useState(user?.name ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<Role>(user?.role ?? 'AGENT');
  const [regionId, setRegionId] = useState(user?.regionId ?? regions[0]?.id ?? '');
  const [extension, setExtension] = useState(user?.extension ?? '');
  const [mobileNumber, setMobileNumber] = useState(user?.mobileNumber ?? '');
  const [disabled, setDisabled] = useState(user?.disabled ?? false);
  const [photoVersion, setPhotoVersion] = useState(user?.photoVersion ?? null);
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    const input = {
      name,
      email,
      role,
      regionId: role === 'TENANT_ADMIN' && !regionId ? null : regionId || null,
      extension,
      mobileNumber: mobileNumber.trim() || null,
    };
    if (user) {
      const saved = await form.submit(updateUserSchema, { ...input, disabled }, (value) => api.updateUser(user.id, value));
      if (saved) onSaved(saved);
    } else {
      const created = await form.submit(createUserSchema, input, api.createUser);
      if (created) onSaved(created.user, created.passwordLink);
    }
  }

  return (
    <form onSubmit={submit} noValidate>
      <div className="dialog-body">
        {!user && regions.length === 0 && <Notice>{t('Add a region first: everyone except admins belongs to one.')}</Notice>}
        {user && (
          <div className="person" style={{ gap: '1rem', marginBottom: '1rem' }}>
            <Avatar name={user.name} photo={photoUrl(user.id, photoVersion)} large />
            <PhotoControls
              userId={user.id}
              version={photoVersion}
              onChange={(version) => {
                setPhotoVersion(version);
                onPhotoChanged();
              }}
            />
          </div>
        )}
        <div className="form-grid">
          <Field label={t('Name')} error={form.fields.name}>
            <input value={name} onChange={(e) => setName(e.target.value)} autoFocus data-testid="person-name" />
          </Field>
          <Field label={t('Email')} error={form.fields.email}>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} data-testid="person-email" />
          </Field>
          <Field label={t('Role')} error={form.fields.role}>
            <select value={role} onChange={(e) => setRole(e.target.value as Role)} data-testid="person-role">
              {roles.map((r) => (
                <option key={r} value={r}>
                  {roleNames[r]}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('Region')} error={form.fields.regionId}>
            <select value={regionId} onChange={(e) => setRegionId(e.target.value)} data-testid="person-region">
              {role === 'TENANT_ADMIN' && <option value="">{t('All regions')}</option>}
              {regions.map((region) => (
                <option key={region.id} value={region.id}>
                  {region.name}
                </option>
              ))}
            </select>
          </Field>
          <Field label={t('Extension')} hint={t('Their internal number')} error={form.fields.extension}>
            <input inputMode="numeric" value={extension} onChange={(e) => setExtension(e.target.value)} data-testid="person-extension" />
          </Field>
          <Field label={t('Mobile number')} hint={t('Optional. For forwarding unanswered calls')} error={form.fields.mobileNumber}>
            <input type="tel" placeholder="+37061234567" value={mobileNumber} onChange={(e) => setMobileNumber(e.target.value)} />
          </Field>
        </div>
        {user && (
          <label className="checkbox">
            <input type="checkbox" checked={disabled} onChange={(e) => setDisabled(e.target.checked)} />
            {t('Disabled: can’t sign in or take calls')}
          </label>
        )}
        {form.error && <Notice kind="error">{form.error}</Notice>}
      </div>
      <div className="dialog-footer">
        <button type="button" className="secondary" onClick={onCancel}>
          {t('Cancel')}
        </button>
        <button type="submit" disabled={form.busy} data-testid="save-person">
          {user ? t('Save changes') : t('Add person')}
        </button>
      </div>
    </form>
  );
}
